import { SaleType } from '../ngrx-store/app.state'

/** Kept in step with corpers.online's utilities/saleOptions.js - the backend rejects anything else. */
export const SALE_CONDITIONS = [
  { value: 'new', label: 'New' },
  { value: 'fairly_used', label: 'Fairly used' },
  { value: 'used', label: 'Used' },
] as const

export const SALE_CATEGORIES = [
  { value: 'bedding', label: 'Bedding & mattresses' },
  { value: 'kitchen', label: 'Kitchen & cooking' },
  { value: 'electronics', label: 'Phones & electronics' },
  { value: 'furniture', label: 'Furniture' },
  { value: 'fashion', label: 'Clothes & shoes' },
  { value: 'nysc_kit', label: 'NYSC kit' },
  { value: 'books', label: 'Books & stationery' },
  { value: 'other', label: 'Other' },
] as const

export type SaleCondition = typeof SALE_CONDITIONS[number]['value']
export type SaleCategory = typeof SALE_CATEGORIES[number]['value']

export function conditionLabel(value: string | null | undefined): string | undefined {
  return SALE_CONDITIONS.find((c) => c.value === value)?.label
}

export function categoryLabel(value: string | null | undefined): string | undefined {
  return SALE_CATEGORIES.find((c) => c.value === value)?.label
}

/** A sold item stays in the feed this long (the backend stops sending it after that too). */
export const SOLD_VISIBLE_MS = 24 * 60 * 60 * 1000

/** False for an item sold more than a day ago - the store can still hold one fetched before then. */
export function isShownInFeed(sale: Pick<SaleType, 'sold_at'>, now = Date.now()): boolean {
  return !sale.sold_at || now - new Date(sale.sold_at).getTime() < SOLD_VISIBLE_MS
}

/** 'LAGOS' / 'FCT - ABUJA' / 'Aba North' -> 'Lagos' / 'FCT - Abuja' / 'Aba North': how places are shown. */
export function titleCase(text: string): string {
  return text.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()).replace(/\bFct\b/, 'FCT')
}

/**
 * 'BAUCHI, Alkaleri' (CorpMember._location: state, then LGA) -> 'Alkaleri, Bauchi' - the LGA is
 * what tells a buyer how near it is. 'FCT - ABUJA' -> 'FCT - Abuja'.
 */
export function saleLocation(location: string | undefined | null): string {
  const [state, lga] = (location ?? '').split(', ')
  return [lga, state && titleCase(state)].filter(Boolean).join(', ')
}

/** 'BAUCHI, Alkaleri' -> 'Bauchi': a listing's state, from its poster's `_location`, as saleLocation() shows it. */
export function locationState(location: string | undefined | null): string | undefined {
  return saleLocation(location).split(', ').pop() || undefined
}

/**
 * How close the seller is to the viewer, from their `_location`s ('STATE, LGA'): 2 for the same
 * LGA, 1 for the same state, else 0. Used to sort the nearest first.
 */
export function nearness(sellerLocation: string | undefined | null, viewerLocation: string | undefined | null): 0 | 1 | 2 {
  const [sellerState, sellerLga] = (sellerLocation ?? '').toLowerCase().split(', ')
  const [viewerState, viewerLga] = (viewerLocation ?? '').toLowerCase().split(', ')
  if (!sellerState || sellerState !== viewerState) return 0
  return sellerLga && sellerLga === viewerLga ? 2 : 1
}

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']

/**
 * "Leaving in March" when the seller's service year ends within the next two months - corps
 * members sell up before they go, so it tells a buyer to be quick. Nothing otherwise, or once
 * the date has passed. `endDate` is a DATEONLY ('2027-03-14').
 */
export function leavingLabel(endDate: string | null | undefined, now = new Date()): string | undefined {
  const [year, month] = (endDate ?? '').split('-').map(Number)
  if (!year || !month) return undefined
  const monthsAway = (year - now.getFullYear()) * 12 + (month - 1 - now.getMonth())
  if (monthsAway < 0 || monthsAway > 2) return undefined
  return monthsAway === 0 ? 'Leaving this month' : `Leaving in ${MONTHS[month - 1]}`
}

/**
 * A Cloudinary image cut to a `size`px square (cropped around what's interesting) in the best
 * format and quality the browser takes - a grid card doesn't need the full upload, which matters
 * on mobile data. Other URLs (uploads from before Cloudinary, test images) come back unchanged.
 */
export function thumbnailUrl(url: string | undefined, size: number): string | undefined {
  if (!url || !/^https:\/\/res\.cloudinary\.com\/[^/]+\/image\/upload\//.test(url)) return url
  return url.replace('/image/upload/', `/image/upload/c_fill,g_auto,w_${size},h_${size},f_auto,q_auto/`)
}
